import type { CourseClass } from "@/api/studyWorkshopDetails.types";
import { cn } from "@/components/utils/utils";
import { useState, useEffect } from "react";
import watchedIcon from "../../assets/workshop/watchedIcon.png";
import unmarkedMedia from "../../assets/workshop/UnmarkedMedia.png";
import { formatDuration } from "@/utils/dateAndTime";
import { useCourseProgress } from "@/context/StorageContext";
import {
	extractFlexiQuizLink,
	getAkademiaDownloadUrl,
	parseVideoBody,
	removeSoundCloudPrestitial,
} from "./MultimediaCard.utils";
import { VideoPlayer } from "./VideoPlayer";
import { AudioPlayer } from "./AudioPlayer";
import { Browser } from "@capacitor/browser";
import { BookCheck, ExternalLink } from "lucide-react";
import { DownloadVideoDrawer } from "@/components/DownloadVideoDrawer";
import {
	getOfflineVideo,
	generateVideoId,
	generateMediaId,
} from "@/utils/storage/offlineVideosStorage";
import {
	downloadVideoWithResume,
	pauseDownload,
	resumeDownload,
	cancelDownload,
} from "@/utils/videoDownloadService";
import {
	downloadAudio,
	USE_SERVER_TRANSMUX,
} from "@/utils/audioDownloadServiceFactory";
import { getVimeoVideoLink, getVimeoAudioStream } from "@/api/api";
import { useQuery } from "@tanstack/react-query";
import type { VimeoResponse } from "@/api/vimeo.types";
import type { ProductTypeMultimedia, ProductDataMultimedia } from "./types";

// Local type for audio stream response
interface AudioStreamData {
	audioStreamUrl: string;
	duration: number;
	format: "m4a" | "mp3";
}
import { DownloadIcon } from "@/components/icons/download";
import { HeadphonesCircleIcon } from "@/components/icons/headphonesCircle";
import { videoLog } from "@/components/VideoDownloadDebugger";
import { PlayIcon } from "@/components/icons/play";
import { PauseIcon } from "@/components/icons/pause";

// Typ dla stanu pobierania
type DownloadStatus =
	| "none"
	| "downloading"
	| "paused"
	| "completed_video"
	| "completed_audio"
	| "failed";

// Helper to check if status is any completed variant
const isCompletedStatusLocal = (status: DownloadStatus): boolean => {
	return status === "completed_video" || status === "completed_audio";
};

const isCompletedVideoStatusLocal = (status: DownloadStatus): boolean => {
	return status === "completed_video";
};

// Re-export types for backward compatibility
export type { ProductTypeMultimedia, ProductDataMultimedia };

export const MultimediaCard = ({
	courseClassData,
	productData,
}: {
	courseClassData: CourseClass;
	productData: ProductDataMultimedia;
}): JSX.Element => {
	const { body, body_audio, duration, name } = courseClassData;
	const videoBody = parseVideoBody(body);
	const flexiQuiz = extractFlexiQuizLink(body);
	const [open, setOpen] = useState<"AUDIO" | "AUDIO_V2" | "VIDEO" | false>(false);
	const [downloadDrawerOpen, setDownloadDrawerOpen] = useState(false);
	const { productId, productType } = productData;

	const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>("none");
	const [downloadProgress, setDownloadProgress] = useState(0);

	const { isCompleted } = useCourseProgress();
	const courseClassesId = courseClassData.id.toString();
	const completed = isCompleted(productId, courseClassesId);

	// Derived states (computed)
	const isDownloaded = isCompletedStatusLocal(downloadStatus);
	const hasLocalVideo = isCompletedVideoStatusLocal(downloadStatus);

	// const videoId = generateVideoId(productType, productId, courseClassesId);
	const videoId = generateMediaId(productType, productId, courseClassesId);
	// Check if video is downloaded
	useEffect(() => {
		const checkDownloadStatus = async (): Promise<void> => {
			// Sprawdź czy video jest w pamięci lokalnej
			const videoMetadata = await getOfflineVideo(videoId);

			// console.log("🔍 [MultimediaCard] Checking download status:", {
			// 	videoId,
			// 	found: !!videoMetadata,
			// 	status: videoMetadata?.status,
			// 	productType,
			// 	productId,
			// 	courseClassesId,
			// });

			if (videoMetadata) {
				setDownloadProgress(videoMetadata.downloadProgress);

				// ✅ Map storage status to component status
				switch (videoMetadata.status) {
					case "completed_video":
						console.log(
							"✅ [MultimediaCard] Video is downloaded, setting downloadStatus to completed_video"
						);
						setDownloadStatus("completed_video");
						break;
					case "completed_audio":
						console.log(
							"✅ [MultimediaCard] Audio is downloaded, setting downloadStatus to completed_audio"
						);
						setDownloadStatus("completed_audio");
						break;
					case "downloading":
						setDownloadStatus("downloading");
						break;
					case "paused":
						setDownloadStatus("paused");
						break;
					case "failed":
						setDownloadStatus("failed");
						break;
					default:
						// Migration: old "completed" status or unknown -> determine from mediaType
						// If mediaType is undefined or "video", treat as video; if "audio", treat as audio
						if ((videoMetadata.status as string) === "completed") {
							const isAudio = videoMetadata.mediaType === "audio";
							console.log(
								`✅ [MultimediaCard] Migrating old "completed" status -> ${isAudio ? "completed_audio" : "completed_video"}`
							);
							setDownloadStatus(isAudio ? "completed_audio" : "completed_video");
						} else {
							setDownloadStatus("none");
						}
				}
			} else {
				// console.log("❌ [MultimediaCard] Video not found in storage");
				setDownloadStatus("none");
			}
		};
		checkDownloadStatus().catch(console.error);
	}, [productId, courseClassesId, productType]);

	// Okresowe sprawdzanie postępu podczas pobierania
	useEffect(() => {
		if (downloadStatus !== "downloading") return;

		const intervalId = setInterval(() => {
			void (async (): Promise<void> => {
				const videoId = generateVideoId(
					productType,
					productId,
					courseClassesId
				);
				const videoMetadata = await getOfflineVideo(videoId);

				if (videoMetadata) {
					setDownloadProgress(videoMetadata.downloadProgress);

					// Update status if changed
					if (videoMetadata.status === "completed_video") {
						setDownloadStatus("completed_video");
						setDownloadProgress(100);
					} else if (videoMetadata.status === "completed_audio") {
						setDownloadStatus("completed_audio");
						setDownloadProgress(100);
					} else if ((videoMetadata.status as string) === "completed") {
						// Migration: old "completed" status -> determine from mediaType
						const isAudio = videoMetadata.mediaType === "audio";
						setDownloadStatus(isAudio ? "completed_audio" : "completed_video");
						setDownloadProgress(100);
					} else if (videoMetadata.status === "failed") {
						setDownloadStatus("failed");
						setDownloadProgress(0);
					}
				}
			})();
		}, 1000);

		return (): void => {
			clearInterval(intervalId);
		};
	}, [downloadStatus, productId, courseClassesId, productType]);

	// Fetch Vimeo data if needed
	const isVimeo = videoBody.video_source === "vimeo_source";
	const vimeoId = isVimeo ? videoBody.vimeoId : undefined;

	const { data: vimeoVideoLink } = useQuery<VimeoResponse | null>({
		queryKey: ["vimeoVideoLink", vimeoId],
		queryFn: async (): Promise<VimeoResponse | null> => {
			if (!vimeoId) return null;
			const result = await getVimeoVideoLink(vimeoId.toString());
			return result ?? null;
		},
		enabled: isVimeo && !!vimeoId && downloadDrawerOpen,
	});

	// Fetch audio stream URL for audio download option
	const { data: audioStreamData } = useQuery<AudioStreamData | null>({
		queryKey: ["vimeoAudioStream", vimeoId],
		queryFn: async (): Promise<AudioStreamData | null> => {
			if (!vimeoId) return null;
			const result = await getVimeoAudioStream(vimeoId.toString()) as AudioStreamData | undefined;
			return result ?? null;
		},
		enabled: isVimeo && !!vimeoId && downloadDrawerOpen,
	});

	const handleDownloadConfirm = async (
		resolution: string,
		downloadUrl: string,
		sizeMB: number
	): Promise<void> => {
		videoLog.info(
			"UI",
			"User confirmed download",
			{
				videoName: name,
				resolution,
				sizeMB,
				downloadUrl,
			},
			videoId
		);

		setDownloadStatus("downloading");
		try {
			await downloadVideoWithResume(
				courseClassesId,
				productData,
				// productSlug,
				name,
				duration,
				downloadUrl,
				resolution,
				sizeMB,
				videoBody.video_source as "vimeo_source" | "akademia_source",
				isVimeo ? vimeoId : undefined,
				(progress) => {
					setDownloadProgress(progress);
				}
			);

			console.log("✅ Download completed successfully");
			videoLog.success(
				"UI",
				"Download completed successfully",
				{
					videoName: name,
				},
				videoId
			);

			setDownloadStatus("completed_video");
			setDownloadProgress(100);
			alert("Wideo zostało pobrane!");
		} catch (error) {
			console.error("Download error:", error);

			// ✅ Rozróżnij pause od błędu
			if (error instanceof Error && error.message === "DOWNLOAD_PAUSED") {
				console.log("⏸️ Download was paused - status already set");
				videoLog.info("UI", "Download was paused by user", {}, videoId);
				return;
			}

			// Prawdziwy błąd
			videoLog.error(
				"UI",
				"Download failed with error",
				{
					error: error instanceof Error ? error.message : String(error),
					errorName: error instanceof Error ? error.name : undefined,
					videoName: name,
				},
				videoId
			);

			setDownloadStatus("failed");
			setDownloadProgress(0);
			alert("Błąd podczas pobierania wideo");
		}
	};

	const handleAudioDownloadConfirm = async (
		audioStreamUrl: string,
		estimatedSizeMB: number
	): Promise<void> => {
		const implementationType = USE_SERVER_TRANSMUX ? "transmux (MP4)" : "HLS (TS)";
		
		videoLog.info(
			"UI",
			`User confirmed audio download (${implementationType})`,
			{
				audioName: name,
				estimatedSizeMB,
				audioStreamUrl,
				vimeoId,
				useTransmux: USE_SERVER_TRANSMUX,
			},
			videoId
		);

		setDownloadStatus("downloading");
		try {
			// Use factory to download audio (automatically selects implementation)
			await downloadAudio({
				courseClassesId,
				productData,
				audioName: name,
				duration,
				vimeoId: vimeoId || 0,
				audioStreamUrl, // Used only for HLS implementation
				estimatedSizeMB,
				onProgress: (progress, phase) => {
					setDownloadProgress(progress);
					// Log phase changes for transmux implementation
					if (phase) {
						console.log(`🎵 Audio download phase: ${phase} (${Math.round(progress)}%)`);
					}
				},
			});

			console.log("✅ Audio download completed successfully");
			videoLog.success(
				"UI",
				"Audio download completed successfully",
				{
					audioName: name,
					implementation: implementationType,
				},
				videoId
			);

			setDownloadStatus("completed_audio");
			setDownloadProgress(100);
			alert("Audio zostało pobrane!");
		} catch (error) {
			console.error("Audio download error:", error);

			if (error instanceof Error && error.message === "DOWNLOAD_PAUSED") {
				console.log("⏸️ Audio download was paused");
				videoLog.info("UI", "Audio download was paused by user", {}, videoId);
				return;
			}

			videoLog.error(
				"UI",
				"Audio download failed with error",
				{
					error: error instanceof Error ? error.message : String(error),
					audioName: name,
					implementation: implementationType,
				},
				videoId
			);

			setDownloadStatus("failed");
			setDownloadProgress(0);
			alert("Błąd podczas pobierania audio");
		}
	};

	const handlePause = async (): Promise<void> => {
		videoLog.warning(
			"UI",
			"User clicked pause",
			{
				currentProgress: downloadProgress,
			},
			videoId
		);

		try {
			setDownloadStatus("paused");
			await pauseDownload(productType, productId, courseClassesId);
			setDownloadProgress(0);

			videoLog.success("UI", "Download paused successfully", {}, videoId);
			alert("Pobieranie wideo zostało wstrzymane");
		} catch (error) {
			console.error("Pause error:", error);
			videoLog.error(
				"UI",
				"Error pausing download",
				{
					error: error instanceof Error ? error.message : String(error),
				},
				videoId
			);

			alert("Błąd podczas wstrzymywania pobierania");
			setDownloadStatus("downloading"); // Rollback jeśli pause failed
		}
	};

	const handleResume = async (): Promise<void> => {
		videoLog.info(
			"UI",
			"User clicked resume",
			{
				videoName: name,
			},
			videoId
		);

		setDownloadStatus("downloading");
		try {
			await resumeDownload(
				productType,
				productId,
				courseClassesId,
				(progress) => {
					console.log(`Resume progress: ${progress}%`);
					setDownloadProgress(progress);
				}
			);

			videoLog.success("UI", "Resume completed successfully", {}, videoId);
			setDownloadStatus("completed_video");
			setDownloadProgress(100);
		} catch (error) {
			console.error("Resume error:", error);

			if (error instanceof Error && error.message === "DOWNLOAD_PAUSED") {
				console.log("Download paused by user");
				videoLog.info("UI", "Download paused during resume", {}, videoId);
				return;
			}

			videoLog.error(
				"UI",
				"Error resuming download",
				{
					error: error instanceof Error ? error.message : String(error),
				},
				videoId
			);

			alert("Błąd podczas wznawiania pobierania");
			setDownloadStatus("paused");
		}
	};

	const handleCancel = async (): Promise<void> => {
		if (
			!confirm(
				"Czy na pewno chcesz anulować pobieranie? Postęp zostanie utracony."
			)
		) {
			videoLog.info("UI", "User canceled the cancel operation", {}, videoId);
			return;
		}

		videoLog.warning(
			"UI",
			"User confirmed cancel download",
			{
				currentProgress: downloadProgress,
			},
			videoId
		);

		try {
			await cancelDownload(productType, productId, courseClassesId);
			setDownloadStatus("none");
			setDownloadProgress(0);
			console.log("✅ Download canceled successfully");
			videoLog.success("UI", "Download canceled successfully", {}, videoId);
		} catch (error) {
			console.error("Cancel error:", error);
			videoLog.error(
				"UI",
				"Error canceling download",
				{
					error: error instanceof Error ? error.message : String(error),
				},
				videoId
			);
			alert("Błąd podczas anulowania pobierania");
		}
	};

	// SOUNDCLOUD EMBED IMPLEMENTATION
	// const [embedHtml, setEmbedHtml] = useState<string | null>(null);
	// useEffect(() => {
	// 	getSoundCloudOEmbed()
	// 		.then((response) => {
	// 			setEmbedHtml(response.html);
	// 		})
	// 		.catch((err) => {
	// 			console.log("err", err);
	// 		});
	// }, []);

	const processedAudioBody = body_audio
		? removeSoundCloudPrestitial(body_audio)
		: body_audio;

	const openFlexiQuizSite = async (flexiQuiz: string): Promise<void> => {
		await Browser.open({ url: flexiQuiz });
	};

	const onlyQuiz = videoBody.video_source === "unknown" && !!flexiQuiz;
	console.log(`Lesson Name: ${name}, videoBody.video_source: ${videoBody.video_source} flexiQuiz: ${flexiQuiz}, typeOfFlexiQuiz: ${typeof flexiQuiz}, onlyQuiz: ${onlyQuiz}`);

	return (
		<>
			<div className="w-full bg-[#F9FAFB] flex flex-col shadow-[0_20px_20px_-10px_rgb(145,158,171,0.2)] mb-[15px] mt-[19px]">
				<div className="flex w-full mb-[26px]">
					<div className="flex w-full justify-between items-center">
						<div className="flex items-center max-w-[75%] pt-2">
							{onlyQuiz ? (
								<BookCheck className="w-[26px] h-[26px] ml-[14px] mr-[15px]" />
							) : (
								<img
									alt="watched icon"
									className="w-[26px] h-[26px] ml-[14px] mr-[15px]"
									src={completed ? watchedIcon : unmarkedMedia}
								/>
							)}
							<p className="text-sm">{name}</p>
						</div>
						{!onlyQuiz && (
							<p className="mr-[10px] text-[#637381] text-sm">
								{formatDuration(duration)}
							</p>
						)}
					</div>
				</div>
				<div className="text-sm justify-around items-center bg-white rounded-b-xl">
					<div className="flex flex-wrap md:flex-nowrap overflow-hidden">
						{(videoBody.video_source !== "unknown" || hasLocalVideo) && (
							<div className="flex items-center justify-between md:justify-start w-full px-6">
								<p className="mr-2">Oglądaj Video</p>
								<button
									className="p-4"
									onClick={() => {
										setOpen("VIDEO");
									}}
								>
									<PlayIcon />
								</button>
							</div>
						)}
						{/* {body_audio && (
							<div className="flex items-center justify-between md:justify-start w-full px-6">
								<p className="mr-2">Słuchaj Audio</p>
								<button
									className="p-4"
									onClick={() => {
										setOpen("AUDIO");
									}}
								>
									<img alt="play audio icon" src={playAudioIcon} />
								</button>
							</div>
						)} */}
						{/* Audio v2 - Vimeo streaming audio player */}
						{isVimeo && vimeoId && (
							<div className="flex items-center justify-between md:justify-start w-full px-6">
								<p className="mr-2">Słuchaj Audio</p>
								<button
									className="p-4"
									onClick={() => {
										setOpen("AUDIO_V2");
									}}
								>
									<HeadphonesCircleIcon />
								</button>
							</div>
						)}
						{flexiQuiz && (
							<div className="flex items-center justify-between md:justify-start w-full px-6">
								<p className="mr-2">Rozwiąż Quiz</p>
								<button
									className="p-4"
									onClick={async () => {
										await openFlexiQuizSite(flexiQuiz);
									}}
								>
									<ExternalLink />
								</button>
							</div>
						)}
						{videoBody.video_source !== "unknown" &&
							import.meta.env.VITE_DOWNLOAD_VIDEO_FEATURE_FLAG === "true" && (
								<div
									className={cn(
										"flex items-center justify-between md:justify-start w-full px-6",
										downloadStatus === "downloading" && "bg-[#F9FAFB]"
									)}
								>
									<p
										className={cn("mr-2 py-4", isDownloaded && "text-gray-300")}
									>
										{downloadStatus === "completed_video" &&
											"Ten materiał został pobrany jako video"}
										{downloadStatus === "completed_audio" &&
											"Ten materiał został pobrany jako audio"}
										{downloadStatus === "downloading" &&
											`Trwa pobieranie... ${Math.round(downloadProgress)}%`}
										{downloadStatus === "paused" && "Pobieranie wstrzymane"}
										{downloadStatus === "failed" && "Błąd pobierania"}
										{downloadStatus === "none" && "Pobierz, by oglądać offline"}
									</p>
									<div className="flex gap-2">
										{/* Wznów - tylko dla paused */}
										{downloadStatus === "paused" && (
											<button
												className="p-4 text-sm underline text-blue-600"
												onClick={handleResume}
											>
												Wznów
											</button>
										)}

										{/* Wstrzymaj - tylko dla downloading */}
										{downloadStatus === "downloading" && (
											<button
												className="p-4 text-sm underline text-orange-600"
												onClick={handlePause}
											>
												<PauseIcon />
											</button>
										)}

										{/* Anuluj - dla downloading i paused */}
										{(downloadStatus === "downloading" ||
											downloadStatus === "paused") && (
											<button
												className="p-4 text-sm underline text-red-600"
												onClick={handleCancel}
											>
												Anuluj
											</button>
										)}

										{/* Pobierz - tylko dla none i failed */}
										{(downloadStatus === "none" ||
											downloadStatus === "failed") && (
											<button
												className="p-4"
												onClick={() => {
													setDownloadDrawerOpen(true);
												}}
											>
												<DownloadIcon isDownloaded={false} />
											</button>
										)}
									</div>
								</div>
							)}
					</div>
					{downloadStatus === "downloading" && (
						<div className="h-[5px] w-full bg-[#D5E5FF] rounded-b-xl overflow-hidden relative">
							{downloadProgress > 0 && (
								<div
									className="h-full bg-[#196DFE] transition-all duration-300 ease-out"
									style={{ width: `${downloadProgress}%` }}
								/>
							)}
						</div>
					)}
				</div>

				{/* Video Player Modal */}
				<div
					className={cn(
						"fixed inset-0 pt-safe-3 z-50 bg-black",
						open === "VIDEO" || open === "AUDIO" ? "block" : "hidden"
					)}
				>
					{(videoBody.video_source !== "unknown" || hasLocalVideo) &&
						((): JSX.Element => {
							const videoContent = hasLocalVideo
								? { video_source: "local_memory" as const }
								: videoBody;

							// console.log("🎬 [MultimediaCard] Rendering VideoPlayer:", {
							// 	hasLocalVideo,
							// 	downloadStatus,
							// 	videoContent,
							// 	productType,
							// 	productId,
							// 	courseClassesId,
							// });

							// VideoPlayer only accepts "VIDEO" | "AUDIO" | false
							const videoOpen = open === "VIDEO" || open === "AUDIO" ? open : false;
							return (
								<VideoPlayer
									videoContent={videoContent}
									open={videoOpen}
									productData={productData}
									courseClassesId={courseClassesId}
									setOpen={(newOpen) => {
										setOpen(newOpen);
									}}
								/>
							);
						})()}
					{open === "AUDIO" && (
						<>
							<div
								dangerouslySetInnerHTML={{
									__html: processedAudioBody,
								}}
								className="soundcloud-embed absolute top-1/4 left-0 w-[100%]"
							/>
						</>
					)}
				</div>

				{/* Audio V2 Player Modal - Vimeo streaming */}
				{open === "AUDIO_V2" && isVimeo && vimeoId && (
					<div className="fixed inset-0 pt-safe-3 z-50 bg-black">
						<AudioPlayer
							audioContent={{ audio_source: "vimeo_source", vimeoId }}
							open={open}
							productData={productData}
							courseClassesId={courseClassesId}
							courseClassName={name}
							setOpen={(newOpen) => { setOpen(newOpen === false ? false : newOpen); }}
						/>
					</div>
				)}
			</div>

			<DownloadVideoDrawer
				open={downloadDrawerOpen}
				onOpenChange={setDownloadDrawerOpen}
				videoName={name}
				vimeoData={vimeoVideoLink ?? undefined}
				onConfirmDownload={handleDownloadConfirm}
				onConfirmAudioDownload={handleAudioDownloadConfirm}
				videoSource={videoBody.video_source}
				akademiaVideoUrl={
					videoBody.video_source === "akademia_source"
						? videoBody.videoUrl
						: undefined
				}
				estimatedSizeMB={100}
				audioStreamUrl={audioStreamData?.audioStreamUrl}
			/>
		</>
	);
};
